import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BILLING_TYPE_LABELS, BILLING_TYPES, type BillingType, type SessionsQuery } from '@pulsepoint/shared';
import { Download, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMe } from '../../api/auth';
import { useDrivers } from '../../api/drivers';
import { useSites } from '../../api/organisations';
import { localTimezone, sessionsSearch, useSessions, type Session } from '../../api/sessions';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusLabel } from '../../components/StatusLabel';
import { siteLabels } from '../../lib/place';
import { colours } from '../../theme';
import { billedTo, duration, kwhOf, PERIODS, periodRange, whereCharged, whoCharged, type Period } from './format';
import { SessionDialog } from './SessionDialog';

const when = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
const whenWithYear = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const started = (iso: string) => (new Date(iso).getFullYear() === new Date().getFullYear() ? when : whenWithYear).format(new Date(iso));

type Status = 'all' | 'active' | 'completed';
const SORT_KEYS: Record<string, 'started' | 'energy' | 'duration'> = { started: 'started', energy: 'energy', time: 'duration' };

/** Every charging session, live and past, paged by the server, with filters and export. */
export function SessionsPage() {
  const [params, setParams] = useSearchParams();
  const { data: me } = useMe();
  const canExport = usePermission('reports.export');
  const dialog = useDialog();
  const sites = useSites(undefined);
  const manyOrgs = me?.role === 'admin' || (me?.organisationIds?.length ?? 0) > 1;

  // Filters. driverId and chargerId can come in the address (links from a driver or charger).
  const [period, setPeriod] = useState<Period>((params.get('period') as Period) || 'month');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [status, setStatus] = useState<Status>('all');
  const [billing, setBilling] = useState<BillingType | ''>('');
  const [siteId, setSiteId] = useState(params.get('siteId') ?? '');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'started', direction: 'desc' });
  const driverId = params.get('driverId') ?? undefined;
  const chargerId = params.get('chargerId') ?? undefined;
  const drivers = useDrivers('all', undefined, !!driverId);
  const driverName = driverId ? drivers.data?.find((d) => d.id === driverId)?.name : undefined;

  // Search as you type, once typing pauses.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setQ(typed.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [typed]);

  const query: SessionsQuery = useMemo(
    () => ({
      ...periodRange(period, custom),
      status,
      billingType: billing || undefined,
      siteId: siteId || undefined,
      driverId,
      chargerId,
      q: q || undefined,
      sort: SORT_KEYS[sort.key] ?? 'started',
      direction: sort.direction,
      page,
      pageSize: 25,
    }),
    [period, custom, status, billing, siteId, driverId, chargerId, q, sort, page],
  );
  const sessions = useSessions(query);
  const labels = siteLabels(sites.data ?? [], manyOrgs);
  const data = sessions.data;
  const filterChange = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1);
  };

  const columns: Column<Session>[] = [
    {
      key: 'started',
      header: 'Started',
      sortValue: () => null,
      render: (s) => (
        <Box>
          <Typography className="num" sx={{ whiteSpace: 'nowrap' }}>{started(s.startedAt)}</Typography>
          {s.status === 'active' ? <StatusLabel tone="live">Charging</StatusLabel> : s.status === 'aborted' ? <Typography variant="body2" sx={{ color: colours.faultRed }}>Ended unexpectedly</Typography> : null}
        </Box>
      ),
    },
    {
      key: 'who',
      header: 'Driver and card',
      render: (s) => {
        const w = whoCharged(s);
        return (
          <Box>
            <Typography sx={{ fontWeight: 500 }}>{w.title}</Typography>
            <Typography variant="body2" color="textSecondary">{w.sub}</Typography>
          </Box>
        );
      },
    },
    {
      key: 'where',
      header: 'Charger',
      hideBelow: 'md',
      render: (s) => (
        <Box>
          <Typography>{s.chargerName}</Typography>
          <Typography variant="body2" color="textSecondary">{whereCharged(s)}</Typography>
        </Box>
      ),
    },
    { key: 'time', header: 'Time', align: 'right', hideBelow: 'sm', sortValue: () => null, render: (s) => <span className="num" style={{ whiteSpace: 'nowrap' }}>{duration(s.durationSec)}</span> },
    {
      key: 'energy',
      header: 'kWh',
      align: 'right',
      sortValue: () => null,
      render: (s) => (
        <Box>
          <Typography className="num" sx={{ fontWeight: 500 }}>{kwhOf(s.energyWh)}</Typography>
          {s.status === 'active' && s.powerW != null && <Typography variant="body2" color="textSecondary" className="num">{(s.powerW / 1000).toFixed(1)} kW</Typography>}
        </Box>
      ),
    },
    { key: 'billed', header: 'Billed to', hideBelow: 'md', render: (s) => <Typography color={s.billingType === 'unbilled' ? 'textSecondary' : undefined}>{billedTo(s)}</Typography> },
  ];

  const exportUrl = (format: 'xlsx' | 'csv') => {
    const { page: _p, pageSize: _s, ...rest } = query;
    return `/api/sessions/export?${sessionsSearch({ ...rest, format, tz: localTimezone() })}`;
  };
  const [exportMenu, setExportMenu] = useState<HTMLElement | null>(null);

  const lead = !data
    ? ' '
    : `${data.total.toLocaleString('en-AU')} ${data.total === 1 ? 'session' : 'sessions'} · ${kwhOf(data.energyWh, 1)} kWh · ${data.co2Kg.toLocaleString('en-AU')} kg CO₂ saved`;

  const chip = (value: Status, label: string) => (
    <Chip
      key={value}
      label={label}
      onClick={() => filterChange(setStatus)(value)}
      aria-pressed={status === value}
      sx={status === value ? { bgcolor: colours.nearBlack, color: colours.white, '&:hover': { bgcolor: colours.nearBlack } } : { bgcolor: colours.white, border: `1px solid ${colours.hairline}` }}
    />
  );

  return (
    <>
      <PageHeader
        title="Sessions."
        lead={lead}
        actions={
          canExport && (
            <>
              <Button startIcon={<Download size={20} strokeWidth={1.75} />} onClick={(e) => setExportMenu(e.currentTarget)} disabled={!data?.total}>
                Export
              </Button>
              <Menu anchorEl={exportMenu} open={!!exportMenu} onClose={() => setExportMenu(null)}>
                <MenuItem component="a" href={exportUrl('xlsx')} onClick={() => setExportMenu(null)}>Excel (.xlsx)</MenuItem>
                <MenuItem component="a" href={exportUrl('csv')} onClick={() => setExportMenu(null)}>CSV</MenuItem>
              </Menu>
            </>
          )
        }
      />

      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' }, flexWrap: 'wrap', rowGap: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search driver, card or charger"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            sx={{ width: { xs: '100%', md: 280 } }}
            slotProps={{ htmlInput: { 'aria-label': 'Search sessions' }, input: { startAdornment: <InputAdornment position="start"><Search size={18} strokeWidth={1.5} /></InputAdornment> } }}
          />
          <TextField select size="small" value={period} onChange={(e) => filterChange(setPeriod)(e.target.value as Period)} sx={{ width: { xs: '100%', md: 180 } }} slotProps={{ htmlInput: { 'aria-label': 'When' } }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>
          {period === 'custom' && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <TextField size="small" type="date" label="From" value={custom.from} onChange={(e) => filterChange(setCustom)({ ...custom, from: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
              <TextField size="small" type="date" label="To" value={custom.to} onChange={(e) => filterChange(setCustom)({ ...custom, to: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
            </Stack>
          )}
          {(sites.data?.length ?? 0) > 1 && (
            <TextField select size="small" value={siteId} onChange={(e) => filterChange(setSiteId)(e.target.value)} sx={{ width: { xs: '100%', md: 220 } }} slotProps={{ htmlInput: { 'aria-label': 'Where' }, select: { displayEmpty: true } }}>
              <MenuItem value="">Everywhere</MenuItem>
              {(sites.data ?? []).map((s) => ({ id: s.id, label: labels.get(s.id) ?? s.name })).sort((a, b) => a.label.localeCompare(b.label, 'en-AU')).map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField select size="small" value={billing} onChange={(e) => filterChange(setBilling)(e.target.value as BillingType | '')} sx={{ width: { xs: '100%', md: 200 } }} slotProps={{ htmlInput: { 'aria-label': 'Billed to' }, select: { displayEmpty: true } }}>
            <MenuItem value="">Anyone billed</MenuItem>
            {BILLING_TYPES.map((b) => <MenuItem key={b} value={b}>{BILLING_TYPE_LABELS[b]}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={1} role="group" aria-label="Show">
            {chip('all', 'All')}
            {chip('active', 'Charging now')}
            {chip('completed', 'Finished')}
          </Stack>
        </Stack>
        {(driverId || chargerId) && (
          <Box>
            <Chip
              label={driverId ? `Driver: ${driverName ?? '…'}` : `Charger: ${data?.rows[0]?.chargerName ?? '…'}`}
              onDelete={() => setParams({}, { replace: true })}
              deleteIcon={<X size={16} />}
              sx={{ bgcolor: colours.paleGrey }}
            />
          </Box>
        )}
      </Stack>

      <DataTable
        label="Sessions"
        rows={data?.rows}
        columns={columns}
        rowKey={(s) => s.id}
        loading={sessions.isPending}
        error={sessions.error}
        onRetry={() => void sessions.refetch()}
        onRowClick={(s) => void dialog.open(SessionDialog, { sessionId: s.id })}
        server={{
          total: data?.total ?? 0,
          page,
          pageSize: 25,
          onPage: setPage,
          sort,
          onSort: (s) => {
            // Newest, biggest and longest first on the first click.
            setSort(sort.key === s.key ? s : { key: s.key, direction: 'desc' });
            setPage(1);
          },
        }}
        empty={
          <EmptyState title="No sessions here">
            {q || billing || siteId || status !== 'all' || driverId || chargerId ? 'Nothing matches these filters.' : `No charging ${PERIODS.find((p) => p.value === period)?.label.toLowerCase() ?? 'yet'}.`}
          </EmptyState>
        }
      />
    </>
  );
}
